import { useCallback, useEffect, useRef, useState } from "react";

// Precise camera states — never collapse everything into a single "blocked".
export type CameraStatus =
  | "idle" // user hasn't requested the camera yet
  | "initialising"
  | "active"
  | "denied" // permission explicitly denied/blocked for the site
  | "blocked_by_overlay" // request rejected while permission is still "prompt" (Android overlay case)
  | "not_found"
  | "in_use"
  | "security"
  | "unavailable"
  | "unsupported"
  | "error";

export const CAMERA_ERROR_STATES: CameraStatus[] = [
  "denied",
  "blocked_by_overlay",
  "not_found",
  "in_use",
  "security",
  "unavailable",
  "unsupported",
  "error",
];

async function queryCameraPermission(): Promise<PermissionState | "unknown"> {
  try {
    const perms = navigator.permissions as unknown as {
      query?: (d: { name: string }) => Promise<{ state: PermissionState }>;
    };
    const res = await perms?.query?.({ name: "camera" });
    return res?.state ?? "unknown";
  } catch {
    return "unknown";
  }
}

export function useCamera() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [status, setStatus] = useState<CameraStatus>("idle");

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  // Only ever called from an explicit user action.
  const start = useCallback(async () => {
    if (typeof window !== "undefined" && window.isSecureContext === false) {
      setStatus("security");
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus("unsupported");
      return;
    }
    // Release any abandoned stream before re-requesting (prevents false "in use").
    stop();
    setStatus("initialising");

    const get = (video: MediaStreamConstraints["video"]) =>
      navigator.mediaDevices.getUserMedia({ video, audio: false });

    try {
      let stream: MediaStream;
      try {
        stream = await get({ facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } });
      } catch (e) {
        // OverconstrainedError → retry once with relaxed constraints.
        if ((e as DOMException)?.name === "OverconstrainedError" || (e as DOMException)?.name === "ConstraintNotSatisfiedError") {
          stream = await get(true);
        } else {
          throw e;
        }
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => undefined);
      }
      setStatus("active");
    } catch (err) {
      const name = (err as DOMException)?.name;
      switch (name) {
        case "NotAllowedError":
        case "PermissionDeniedError": {
          // Distinguish an explicit denial from Android blocking the prompt (overlay).
          const perm = await queryCameraPermission();
          setStatus(perm === "denied" ? "denied" : "blocked_by_overlay");
          break;
        }
        case "NotFoundError":
        case "DevicesNotFoundError":
          setStatus("not_found");
          break;
        case "NotReadableError":
        case "TrackStartError":
          setStatus("in_use");
          break;
        case "SecurityError":
          setStatus("security");
          break;
        case "AbortError":
          setStatus("error");
          break;
        default:
          setStatus("error");
      }
    }
  }, [stop]);

  const captureFrame = useCallback((): string | null => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return null;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")?.drawImage(video, 0, 0);
    return canvas.toDataURL("image/jpeg", 0.9);
  }, []);

  useEffect(() => stop, [stop]);

  return { videoRef, status, start, stop, captureFrame };
}

export const isTouchDevice = () =>
  typeof window !== "undefined" && (navigator.maxTouchPoints > 0 || window.matchMedia("(pointer: coarse)").matches);
